import React from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  View,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";

import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { formatPhoneNumberDisplay } from "../../../../../utils/phoneNumber";
import {
  BottomTrayModal,
  PrimaryButton,
  TitleText,
} from "../../../../common/native";
import { styles } from "../HomeConversations.styles";
import type {
  TMessageConversationListItem,
  TMessageSaveStatusFilter,
} from "../types";
import MessageConversationItem from "./MessageConversationItem";

type MessagesInboxCardProps = {
  isMessagesTab: boolean;
  hideOnMobile: boolean;
  loading: boolean;
  error: string | null;
  conversations: TMessageConversationListItem[];
  assignedNumberOptions: string[];
  selectedAssignedNumberE164: string | null;
  selectedConversationUuid: string | null;
  selectedSaveStatuses: TMessageSaveStatusFilter[];
  showUnreadOnly: boolean;
  assignSubmitting: boolean;
  isComposingNewMessage: boolean;
  onAssignedNumberChange: (value: string | null) => void;
  onSaveStatusesChange: (statuses: TMessageSaveStatusFilter[]) => void;
  onShowUnreadOnlyChange: (value: boolean) => void;
  onStartNewMessage: () => void;
  onSelectConversation: (conversationUuid: string) => void;
  onAssignToPatient: (conversation: TMessageConversationListItem) => void;
  onRetry: () => void;
  formatDateTime: (value?: string | null) => string;
};

const DEFAULT_SAVE_STATUSES: TMessageSaveStatusFilter[] = ["saved", "unsaved"];

const MessagesInboxCard: React.FC<MessagesInboxCardProps> = ({
  isMessagesTab,
  hideOnMobile,
  loading,
  error,
  conversations,
  assignedNumberOptions,
  selectedAssignedNumberE164,
  selectedConversationUuid,
  selectedSaveStatuses,
  showUnreadOnly,
  assignSubmitting,
  isComposingNewMessage,
  onAssignedNumberChange,
  onSaveStatusesChange,
  onShowUnreadOnlyChange,
  onStartNewMessage,
  onSelectConversation,
  onAssignToPatient,
  onRetry,
  formatDateTime,
}) => {
  const [filterSheetVisible, setFilterSheetVisible] =
    React.useState<boolean>(false);
  const [draftAssignedNumberE164, setDraftAssignedNumberE164] = React.useState<
    string | null
  >(selectedAssignedNumberE164);
  const [draftSaveStatuses, setDraftSaveStatuses] =
    React.useState<TMessageSaveStatusFilter[]>(selectedSaveStatuses);
  const [draftShowUnreadOnly, setDraftShowUnreadOnly] =
    React.useState<boolean>(showUnreadOnly);

  const includeSaved = selectedSaveStatuses.includes("saved");
  const includeUnsaved = selectedSaveStatuses.includes("unsaved");
  const selectedAssignedNumberLabel = selectedAssignedNumberE164
    ? formatPhoneNumberDisplay(selectedAssignedNumberE164, {
        fallback: selectedAssignedNumberE164,
      })
    : "All Numbers";
  const unreadCount = conversations.reduce(
    (total, conversation) => total + Math.max(conversation.unreadCount || 0, 0),
    0,
  );

  const activeFilterChips = React.useMemo(
    () => [
      ...(selectedAssignedNumberE164
        ? [
            {
              key: "assigned-number",
              label: `# ${selectedAssignedNumberLabel}`,
              onRemove: () => onAssignedNumberChange(null),
            },
          ]
        : []),
      ...(!includeSaved || !includeUnsaved
        ? [
            {
              key: "save-status",
              label: includeSaved
                ? "Saved"
                : includeUnsaved
                  ? "Unsaved"
                  : "No Save Status",
              onRemove: () => onSaveStatusesChange(DEFAULT_SAVE_STATUSES),
            },
          ]
        : []),
      ...(showUnreadOnly
        ? [
            {
              key: "read-status",
              label: "Unread Only",
              onRemove: () => onShowUnreadOnlyChange(false),
            },
          ]
        : []),
    ],
    [
      includeSaved,
      includeUnsaved,
      onAssignedNumberChange,
      onSaveStatusesChange,
      onShowUnreadOnlyChange,
      selectedAssignedNumberE164,
      selectedAssignedNumberLabel,
      showUnreadOnly,
    ],
  );
  const activeFilterCount = activeFilterChips.length;

  const openFilterSheet = React.useCallback(() => {
    setDraftAssignedNumberE164(selectedAssignedNumberE164);
    setDraftSaveStatuses(selectedSaveStatuses);
    setDraftShowUnreadOnly(showUnreadOnly);
    setFilterSheetVisible(true);
  }, [selectedAssignedNumberE164, selectedSaveStatuses, showUnreadOnly]);

  const toggleDraftSaveStatus = React.useCallback(
    (status: TMessageSaveStatusFilter) => {
      setDraftSaveStatuses((currentStatuses) =>
        currentStatuses.includes(status)
          ? currentStatuses.filter((item) => item !== status)
          : [...currentStatuses, status],
      );
    },
    [],
  );

  const clearDraftFilters = React.useCallback(() => {
    setDraftAssignedNumberE164(null);
    setDraftSaveStatuses(DEFAULT_SAVE_STATUSES);
    setDraftShowUnreadOnly(false);
  }, []);

  const applyDraftFilters = React.useCallback(() => {
    onAssignedNumberChange(draftAssignedNumberE164);
    onSaveStatusesChange(draftSaveStatuses);
    onShowUnreadOnlyChange(draftShowUnreadOnly);
    setFilterSheetVisible(false);
  }, [
    draftAssignedNumberE164,
    draftSaveStatuses,
    draftShowUnreadOnly,
    onAssignedNumberChange,
    onSaveStatusesChange,
    onShowUnreadOnlyChange,
  ]);

  return (
    <View
      style={[
        styles.messagesInboxPanel,
        !isMessagesTab && styles.hiddenSection,
        hideOnMobile && styles.hiddenSection,
      ]}
    >
      <TitleText text="Messages" />

      <View style={styles.rowBetween}>
        <Text style={styles.messagesInboxToolbarTitle}>
          Inbox ({unreadCount} new)
        </Text>
        {loading ? (
          <ActivityIndicator size="small" color={ADMIN_TOKENS.color.accent} />
        ) : null}
      </View>

      <View style={styles.messagesInboxToolbar}>
        <View style={styles.messageInboxCompactToolbarRow}>
          <Pressable
            style={[styles.inlineOptionButton, styles.messageFilterButton]}
            onPress={openFilterSheet}
            accessibilityLabel="Open message filters"
          >
            <Ionicons
              name="options-outline"
              size={16}
              color={ADMIN_TOKENS.color.textSecondary}
            />
            <Text style={styles.inlineOptionText}>
              {activeFilterCount > 0
                ? `Filters (${activeFilterCount})`
                : "Filters"}
            </Text>
          </Pressable>
          <View style={styles.messageInboxHeaderActions}>
            <PrimaryButton
              text="New Message"
              onPress={onStartNewMessage}
              style={styles.messageNewButton}
            />
          </View>
        </View>
      </View>

      {activeFilterChips.length ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.messageFilterChipRow}
        >
          {activeFilterChips.map((chip) => (
            <Pressable
              key={chip.key}
              style={styles.messageFilterChip}
              onPress={chip.onRemove}
              accessibilityLabel={`Remove ${chip.label} filter`}
            >
              <Text style={styles.messageFilterChipText}>{chip.label}</Text>
              <Ionicons
                name="close"
                size={14}
                color={ADMIN_TOKENS.color.accentDark}
              />
            </Pressable>
          ))}
        </ScrollView>
      ) : null}

      {loading ? (
        <View style={styles.loadingInlineRow}>
          <ActivityIndicator size="small" color={ADMIN_TOKENS.color.accent} />
          <Text style={styles.loadingText}>Loading conversations...</Text>
        </View>
      ) : error ? (
        <View style={styles.sectionStack}>
          <Text style={styles.inlineErrorText}>{error}</Text>
          <Pressable
            style={styles.callAgainButton}
            onPress={onRetry}
            accessibilityLabel="Retry loading conversations"
          >
            <Text style={styles.callAgainButtonText}>Retry</Text>
          </Pressable>
        </View>
      ) : !conversations.length ? (
        <Text style={styles.emptyText}>
          No conversations yet. Incoming or outbound SMS threads will appear
          here.
        </Text>
      ) : (
        <View style={styles.messagesInboxList}>
          {conversations.map((conversation) => (
            <MessageConversationItem
              key={conversation.conversationUuid}
              conversation={conversation}
              selected={
                selectedConversationUuid === conversation.conversationUuid
              }
              onPress={onSelectConversation}
              onAssignToPatient={onAssignToPatient}
              assignSubmitting={assignSubmitting}
              formatDateTime={formatDateTime}
            />
          ))}
        </View>
      )}

      <BottomTrayModal
        visible={filterSheetVisible}
        onClose={() => setFilterSheetVisible(false)}
        title="Filters"
        maxContentWidth={700}
        footer={
          <View style={styles.messageFilterSheetActionsRow}>
            <Pressable
              style={styles.callAgainButton}
              onPress={clearDraftFilters}
              accessibilityLabel="Clear all filters"
            >
              <Text style={styles.callAgainButtonText}>Clear All</Text>
            </Pressable>
            <PrimaryButton text="Apply" onPress={applyDraftFilters} />
          </View>
        }
      >
        <ScrollView
          style={styles.messageFilterSheetBody}
          contentContainerStyle={styles.messageFilterSheetBodyContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.sectionStack}>
            <Text style={styles.messagesFilterLabel}>Assigned Number</Text>
            <View style={styles.messageFilterOptionsStack}>
              <Pressable
                style={[
                  styles.inlineOptionButton,
                  !draftAssignedNumberE164 && styles.inlineOptionButtonActive,
                ]}
                onPress={() => setDraftAssignedNumberE164(null)}
                accessibilityLabel="Show messages from all assigned numbers"
              >
                <Text
                  style={[
                    styles.inlineOptionText,
                    !draftAssignedNumberE164 && styles.inlineOptionTextActive,
                  ]}
                >
                  All Numbers
                </Text>
              </Pressable>
              {assignedNumberOptions.map((numberE164) => {
                const formattedNumber = formatPhoneNumberDisplay(numberE164, {
                  fallback: numberE164,
                });
                const isSelected = draftAssignedNumberE164 === numberE164;
                return (
                  <Pressable
                    key={numberE164}
                    style={[
                      styles.inlineOptionButton,
                      isSelected && styles.inlineOptionButtonActive,
                    ]}
                    onPress={() => setDraftAssignedNumberE164(numberE164)}
                    accessibilityLabel={`Show messages from ${formattedNumber}`}
                  >
                    <Text
                      style={[
                        styles.inlineOptionText,
                        isSelected && styles.inlineOptionTextActive,
                      ]}
                    >
                      {formattedNumber}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <View style={styles.sectionStack}>
            <Text style={styles.messagesFilterLabel}>Save Status</Text>
            <View style={styles.messageFilterPillRow}>
              <Pressable
                style={[
                  styles.inlineOptionButton,
                  draftSaveStatuses.includes("saved") &&
                    styles.inlineOptionButtonActive,
                ]}
                onPress={() => toggleDraftSaveStatus("saved")}
                accessibilityLabel="Toggle saved numbers"
              >
                <Text
                  style={[
                    styles.inlineOptionText,
                    draftSaveStatuses.includes("saved") &&
                      styles.inlineOptionTextActive,
                  ]}
                >
                  Saved Numbers
                </Text>
              </Pressable>
              <Pressable
                style={[
                  styles.inlineOptionButton,
                  draftSaveStatuses.includes("unsaved") &&
                    styles.inlineOptionButtonActive,
                ]}
                onPress={() => toggleDraftSaveStatus("unsaved")}
                accessibilityLabel="Toggle unsaved numbers"
              >
                <Text
                  style={[
                    styles.inlineOptionText,
                    draftSaveStatuses.includes("unsaved") &&
                      styles.inlineOptionTextActive,
                  ]}
                >
                  Unsaved Numbers
                </Text>
              </Pressable>
            </View>
          </View>

          <View style={styles.sectionStack}>
            <Text style={styles.messagesFilterLabel}>Read Status</Text>
            <View style={styles.messageFilterPillRow}>
              <Pressable
                style={[
                  styles.inlineOptionButton,
                  !draftShowUnreadOnly && styles.inlineOptionButtonActive,
                ]}
                onPress={() => setDraftShowUnreadOnly(false)}
                accessibilityLabel="Show all conversations"
              >
                <Text
                  style={[
                    styles.inlineOptionText,
                    !draftShowUnreadOnly && styles.inlineOptionTextActive,
                  ]}
                >
                  All
                </Text>
              </Pressable>
              <Pressable
                style={[
                  styles.inlineOptionButton,
                  draftShowUnreadOnly && styles.inlineOptionButtonActive,
                ]}
                onPress={() => setDraftShowUnreadOnly(true)}
                accessibilityLabel="Show unread conversations"
              >
                <Text
                  style={[
                    styles.inlineOptionText,
                    draftShowUnreadOnly && styles.inlineOptionTextActive,
                  ]}
                >
                  Unread Only
                </Text>
              </Pressable>
            </View>
          </View>
        </ScrollView>
      </BottomTrayModal>
    </View>
  );
};

export default MessagesInboxCard;
